import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { formatCurrencyForDisplay } from '@/lib/utils';
import type { AddOnScheduleData } from '@/features/booking/types/ticketBookingTypes';


interface GuestAddOnTooltipProps {
  guestIndex: number;
  guestAddOns: string[];
  allAddOns: AddOnScheduleData[];
  children: React.ReactNode;
  /** Currency code for price formatting (e.g., 'GBP', 'USD') */
  currency?: string;
}

const GuestAddOnTooltip: React.FC<GuestAddOnTooltipProps> = ({
  guestIndex,
  guestAddOns,
  allAddOns,
  children,
  currency
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLDivElement>(null);
  // Format price using currency if provided, otherwise show raw amount
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  const getAddOnDetails = (addOnId: string) => {
    return allAddOns.find(addOnSchedule => addOnSchedule.add_on.id === addOnId);
  };

  const addOnDetails = guestAddOns
    .map(getAddOnDetails)
    .filter(Boolean) as AddOnScheduleData[];

  useEffect(() => {
    if (isHovered && buttonRef.current) {
      const updatePosition = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          // Position tooltip: bottom edge 8px above button top, centered horizontally
          const buttonCenterX = rect.left + rect.width / 2;
          setTooltipStyle({
            position: 'fixed',
            bottom: `${window.innerHeight - rect.top + 8}px`, // 8px above button top
            left: `${buttonCenterX}px`, // Center of button (will be centered by x: '-50%' in motion)
            zIndex: 99999,
            pointerEvents: 'none',
          });
        }
      };

      updatePosition();
      const interval = setInterval(updatePosition, 100);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        clearInterval(interval);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isHovered]);

  const tooltipContent = (
    <AnimatePresence>
      {isHovered && (
        <motion.div
          style={tooltipStyle}
          initial={{ opacity: 0, y: 10, scale: 0.9, x: '-50%' }}
          animate={{ opacity: 1, y: 0, scale: 1, x: '-50%' }}
          exit={{ opacity: 0, y: 10, scale: 0.9, x: '-50%' }}
          transition={{ duration: 0.15, ease: "easeOut" }}
        >
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-2xl min-w-48 max-w-64">
            <div className="text-xs font-semibold text-gray-300 mb-2">
              Guest {guestIndex + 1} Add-ons
            </div>
            {addOnDetails.length === 0 ? (
              <div className="text-xs text-gray-500">No add-ons selected</div>
            ) : (
              <div className="space-y-1">
                {addOnDetails.map((addOnSchedule) => (
                  <div key={addOnSchedule.add_on.id} className="flex items-center justify-between">
                    <span className="text-xs text-white truncate max-w-32">
                      {addOnSchedule.add_on.name}
                    </span>
                    <span className="text-xs text-blue-400 ml-2">
                      {formatPrice(addOnSchedule.price)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {/* Arrow pointing down to button */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-700"></div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <div 
        ref={buttonRef}
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
      </div>
      {typeof document !== 'undefined' && createPortal(tooltipContent, document.body)}
    </>
  );
};

export default GuestAddOnTooltip;
